import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, Chip, IconButton, Button, Container } from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { getPurchaseInvoices, deletePurchaseInvoice } from "../services/purchaseService";
import { toast } from 'react-toastify';

function PurchaseOrdersListPage() {
  const [purchaseInvoices, setPurchaseInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadPurchaseInvoices();
  }, []);

  const loadPurchaseInvoices = async () => {
    try {
      setLoading(true);
      const data = await getPurchaseInvoices();
      setPurchaseInvoices(Array.isArray(data.payload) ? data.payload : Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading purchase invoices:', error);
      toast.error('Failed to load purchase invoices');
      setPurchaseInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this purchase invoice?')) {
      try {
        await deletePurchaseInvoice(id);
        toast.success('Purchase invoice deleted successfully');
        loadPurchaseInvoices();
      } catch (error) {
        console.error('Error deleting purchase invoice:', error);
        toast.error('Failed to delete purchase invoice');
      }
    }
  };

  const columns = [
    {
      field: "invoiceNumber",
      headerName: "Invoice Number",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "supplierName",
      headerName: "Supplier",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "purchaseDate",
      headerName: "Date",
      flex: 1,
      minWidth: 110,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: "itemsCount",
      headerName: "Items Count",
      flex: 0.7,
      minWidth: 100,
      renderCell: (params) => (
        <Chip label={params.row.items?.length || 0} color="info" size="small" />
      ),
      sortable: false,
      filterable: false,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box>
          <IconButton
            color="primary"
            onClick={() => navigate(`/purchases/${params.row.id}`)}
            title="View Details"
            size="small"
          >
            <VisibilityIcon />
          </IconButton>
          <IconButton
            color="secondary"
            onClick={() => navigate(`/purchases/edit/${params.row.id}`)}
            title="Edit"
            size="small"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleDelete(params.row.id)}
            title="Delete"
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    },
  ];

  const rows = purchaseInvoices.map((invoice) => ({
    ...invoice,
    itemsCount: invoice.items?.length || 0,
  }));

  return (
    <Box sx={{ background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)', minHeight: '100vh', pt: 3, pb: 8 }}>
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 4, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ bgcolor: alpha("#1976d2", 0.15), p: 1.2, borderRadius: '12px', mr: 2 }}>
              <InventoryIcon sx={{ color: "#1976d2" }} fontSize="large" />
            </Box>
            <Typography variant="h5" fontWeight="bold" color="primary">
              Purchase Invoices
            </Typography>
            <Chip
              label={purchaseInvoices.length}
              color="primary"
              sx={{ ml: 2, fontWeight: 'bold', fontSize: '1em', borderRadius: '8px' }}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              sx={{
                borderRadius: '10px',
                fontWeight: 'bold',
                textTransform: 'none',
                ml: 2
              }}
              onClick={() => navigate("/purchases/add")}
            >
              Add Purchase Order
            </Button>
          </Box>
          <Box sx={{ height: 600, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 20]}
              disableSelectionOnClick
              loading={loading}
              autoHeight={false}
              getRowId={(row) => row.id}
              sx={{
                borderRadius: 2,
                bgcolor: "#fff",
                fontSize: 15,
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: alpha("#1976d2", 0.07),
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-row": {
                  bgcolor: "#fff",
                },
              }}
            />
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default PurchaseOrdersListPage;